// Permisos (DOMAIN §11, PER-*). El servidor aplica todo; la web usa estas mismas funciones solo para
// mostrar u ocultar. La matriz PER-10 está como datos (`PERMISSION_MATRIX`) y `check` la evalúa con el
// contexto de la acción: módulo (PER-05) → alcance del local (PER-02) → rol y parámetros (PER-10) →
// topes y reglas propias (CAJ-13, CAJ-60, CAJ-72, CAJ-74, CLI-60, CLI-62, PER-04).
import { fmtARS, fmtRate } from './format';
import { error, issue, type Issue } from './issues';
import { msg } from './messages';
import { MODULES, isModuleActive, moduleDisabledIssue, type ModuleState } from './modules';
import { withinDiscountCap } from './money';
import { roleAtLeast, type BusinessSettings, type LocationSettings } from './settings';
import type {
  CashMovementType,
  Id,
  ISODate,
  Instant,
  LineKind,
  ModuleKey,
  Pct,
  Pesos,
  Role,
  SaleStatus,
} from './types';

// ───────────────────────── acciones ─────────────────────────

/** Todas las acciones de la matriz PER-10, en su orden. */
export const ACTIONS = [
  // Agenda
  'appointment.view',
  'appointment.create',
  'appointment.edit',
  'appointment.cancel',
  'appointment.noShow',
  'appointment.reopen',
  'appointment.complete',
  'appointment.uncomplete',
  'appointment.overbook',
  'appointment.anyStaff',
  'appointment.markSeen',
  'reminder.send',
  'schedule.hours',
  'schedule.exceptions',
  'booking.link',
  // Clientes
  'client.view',
  'client.viewContact',
  'client.create',
  'client.edit',
  'client.editContact',
  'client.message',
  'client.merge',
  'client.delete',
  'client.anonymize',
  'client.export',
  // Caja
  'cash.view',
  'sale.create',
  'sale.walkin',
  'sale.editPrice',
  'sale.discount',
  'sale.backdate',
  'sale.void',
  'sale.voidSettled',
  'sale.viewCommission',
  'cash.expense',
  'cash.income',
  'cash.voidMovement',
  'cashSession.close',
  'cashSession.reopen',
  'paymentMethod.manage',
  'expenseCategory.manage',
  // Gift cards
  'giftcard.view',
  'giftcard.sell',
  'giftcard.redeem',
  'giftcard.acceptExpired',
  'giftcard.void',
  // Stock
  'stock.view',
  'stock.move',
  'stock.reverse',
  'product.manage',
  'recipe.manage',
  // Comisiones
  'commission.viewOwn',
  'commission.viewAll',
  'settlement.create',
  'settlement.void',
  'advance.create',
  'commissionRule.manage',
  // Finanzas y tablero
  'finance.view',
  'fixedCost.manage',
  'history.manage',
  'ipc.manage',
  'dashboard.view',
  // Ajustes
  'settings.business',
  'settings.location',
  'location.create',
  'location.archive',
  'team.manage',
  'user.manage',
  'audit.view',
  'data.exportAll',
  // Personal
  'profile.edit',
] as const;

export type Action = (typeof ACTIONS)[number];

export function isAction(v: unknown): v is Action {
  return typeof v === 'string' && (ACTIONS as readonly string[]).includes(v);
}

/** Texto de `"No tenés permiso para {descripción}."` de cada acción. */
export const ACTION_DESCRIPTIONS: Readonly<Record<Action, string>> = {
  'appointment.view': 'ver este turno',
  'appointment.create': 'dar turnos',
  'appointment.edit': 'editar este turno',
  'appointment.cancel': 'cancelar este turno',
  'appointment.noShow': 'marcar que no vino',
  'appointment.reopen': 'volver el turno a reservado',
  'appointment.complete': 'marcar el turno como atendido',
  'appointment.uncomplete': 'deshacer un turno atendido',
  'appointment.overbook': 'guardar un turno encimado',
  'appointment.anyStaff': 'manejar turnos de otras personas del equipo',
  'appointment.markSeen': 'marcar esta reserva como vista',
  'reminder.send': 'mandar recordatorios',
  'schedule.hours': 'cambiar los horarios',
  'schedule.exceptions': 'cargar feriados y ausencias',
  'booking.link': 'ver el link de reservas',
  'client.view': 'ver esta ficha',
  'client.viewContact': 'ver los datos de contacto',
  'client.create': 'crear fichas',
  'client.edit': 'editar esta ficha',
  'client.editContact': 'cambiar los datos de contacto',
  'client.message': 'mandar mensajes a clientes',
  'client.merge': 'unir fichas',
  'client.delete': 'borrar fichas',
  'client.anonymize': 'anonimizar fichas',
  'client.export': 'exportar los datos de un cliente',
  'cash.view': 'ver la caja',
  'sale.create': 'cobrar',
  'sale.walkin': 'registrar ventas sin turno',
  'sale.editPrice': 'cambiar precios',
  'sale.discount': 'hacer descuentos',
  'sale.backdate': 'cobrar con una fecha anterior',
  'sale.void': 'anular cobros',
  'sale.voidSettled': 'anular cobros ya liquidados',
  'sale.viewCommission': 'ver las comisiones de este cobro',
  'cash.expense': 'registrar gastos',
  'cash.income': 'registrar ingresos',
  'cash.voidMovement': 'anular este movimiento',
  'cashSession.close': 'abrir y cerrar la caja',
  'cashSession.reopen': 'reabrir la caja',
  'paymentMethod.manage': 'cambiar los medios de pago',
  'expenseCategory.manage': 'cambiar las categorías de gastos',
  'giftcard.view': 'ver gift cards',
  'giftcard.sell': 'vender gift cards',
  'giftcard.redeem': 'cobrar con gift cards',
  'giftcard.acceptExpired': 'aceptar gift cards vencidas',
  'giftcard.void': 'anular gift cards',
  'stock.view': 'ver el stock',
  'stock.move': 'registrar movimientos de stock',
  'stock.reverse': 'revertir movimientos de stock',
  'product.manage': 'cambiar productos',
  'recipe.manage': 'cambiar los insumos de los servicios',
  'commission.viewOwn': 'ver estas comisiones',
  'commission.viewAll': 'ver las comisiones del equipo',
  'settlement.create': 'liquidar comisiones',
  'settlement.void': 'anular liquidaciones',
  'advance.create': 'registrar adelantos',
  'commissionRule.manage': 'cambiar las comisiones',
  'finance.view': 'ver las finanzas',
  'fixedCost.manage': 'cambiar los gastos fijos',
  'history.manage': 'cargar el historial',
  'ipc.manage': 'cargar el IPC',
  'dashboard.view': 'ver el tablero',
  'settings.business': 'cambiar los ajustes del negocio',
  'settings.location': 'cambiar los ajustes del local',
  'location.create': 'crear locales',
  'location.archive': 'archivar locales',
  'team.manage': 'cambiar el equipo y los servicios',
  'user.manage': 'administrar usuarios',
  'audit.view': 'ver la auditoría',
  'data.exportAll': 'exportar todos los datos',
  'profile.edit': 'cambiar tu cuenta',
};

/**
 * PER-05: módulo de cada acción. Una lista = alcanza con que **cualquiera** esté activo. Las que no
 * figuran son del núcleo (MOD-01).
 */
export const ACTION_MODULE: Readonly<Partial<Record<Action, ModuleKey | readonly ModuleKey[]>>> = {
  'cash.view': 'sales',
  'sale.create': 'sales',
  'sale.walkin': 'sales',
  'sale.editPrice': 'sales',
  'sale.discount': 'sales',
  'sale.backdate': 'sales',
  'sale.void': 'sales',
  'sale.voidSettled': 'sales',
  'sale.viewCommission': 'commissions',
  'cash.expense': 'sales',
  'cash.income': 'sales',
  'cash.voidMovement': 'sales',
  'cashSession.close': 'sales',
  'cashSession.reopen': 'sales',
  'paymentMethod.manage': 'sales',
  // finance requiere sales: cubre Cobros y caja y Finanzas en Ajustes (UX §12.1).
  'expenseCategory.manage': 'sales',
  'giftcard.view': 'giftcards',
  'giftcard.sell': 'giftcards',
  'giftcard.redeem': 'giftcards',
  'giftcard.acceptExpired': 'giftcards',
  'giftcard.void': 'giftcards',
  'stock.view': 'stock',
  'stock.move': 'stock',
  'stock.reverse': 'stock',
  'product.manage': 'stock',
  'recipe.manage': 'stock',
  'commission.viewOwn': 'commissions',
  'commission.viewAll': 'commissions',
  'settlement.create': 'commissions',
  'settlement.void': 'commissions',
  'advance.create': 'commissions',
  'commissionRule.manage': 'commissions',
  'finance.view': 'finance',
  'fixedCost.manage': 'finance',
  'history.manage': ['finance', 'dashboard'],
  'ipc.manage': ['finance', 'dashboard'],
  'dashboard.view': 'dashboard',
};

/**
 * Acciones que solo existen con un módulo **apagado**: "Atendido" reemplaza a "Cobrar" sin `sales`
 * (AGE-40, MOD-05, D-17). Con `sales` activo el turno se cierra cobrándolo.
 */
export const ACTION_REQUIRES_MODULE_OFF: Readonly<Partial<Record<Action, ModuleKey>>> = {
  'appointment.complete': 'sales',
};

/**
 * Acciones del negocio (no de un local): `check` no mira el alcance del local para ellas, y la API
 * puede llamarlas con `locationId: null`. Las demás también aceptan `null` (por ejemplo, clientes,
 * que son del negocio, D-03): entonces no se mira el alcance y lo "propio" del profesional no aplica.
 */
export const BUSINESS_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  'settings.business',
  'location.create',
  'user.manage',
  'audit.view',
  'data.exportAll',
  'expenseCategory.manage',
  'ipc.manage',
  'profile.edit',
]);

// ───────────────────────── matriz PER-10 (datos) ─────────────────────────

/** Parámetros de los que depende una celda (§13; `team.*`, `cash.*`, `commissions.*` del local; `sales.*`, `giftCards.*` del negocio). */
export type PolicyCondition =
  | 'team.staffCanCharge'
  | 'team.staffCanOverbook'
  | 'team.staffSeesFullPhone'
  | 'sales.priceOverrideMinRole=manager'
  | 'cash.managerCanVoid'
  | 'cash.managerCanVoid&!cash.voidSettledRequiresOwner'
  | 'commissions.managerCanAdvance'
  | 'giftCards.acceptExpiredRole!=nobody'
  | 'giftCards.acceptExpiredRole=manager';

export const POLICY_CONDITIONS: Readonly<
  Record<PolicyCondition, (loc: LocationSettings, biz: BusinessSettings) => boolean>
> = {
  'team.staffCanCharge': (l) => l.team.staffCanCharge,
  'team.staffCanOverbook': (l) => l.team.staffCanOverbook,
  'team.staffSeesFullPhone': (l) => l.team.staffSeesFullPhone,
  'sales.priceOverrideMinRole=manager': (_l, b) => b.sales.priceOverrideMinRole === 'manager',
  'cash.managerCanVoid': (l) => l.cash.managerCanVoid,
  'cash.managerCanVoid&!cash.voidSettledRequiresOwner': (l) =>
    l.cash.managerCanVoid && !l.cash.voidSettledRequiresOwner,
  'commissions.managerCanAdvance': (l) => l.commissions.managerCanAdvance,
  'giftCards.acceptExpiredRole!=nobody': (_l, b) => b.giftCards.acceptExpiredRole !== 'nobody',
  'giftCards.acceptExpiredRole=manager': (_l, b) => b.giftCards.acceptExpiredRole === 'manager',
};

/**
 * Celda de la matriz: `yes` ✔ · `no` ✘ · `own` propio (PER-03) · `related` fichas relacionadas
 * (CLI-70) · `limited` ✔ con un tope o restricción propia (ver `check`) · `{ if, then }` depende de
 * un parámetro (si no se cumple, ✘).
 */
export type Grant =
  | 'yes'
  | 'no'
  | 'own'
  | 'related'
  | 'limited'
  | { readonly if: PolicyCondition; readonly then: 'yes' | 'own' | 'related' };

const Y = 'yes' as const;
const N = 'no' as const;
const OWN = 'own' as const;
const REL = 'related' as const;
const LIM = 'limited' as const;
const row = (owner: Grant, manager: Grant, staff: Grant): Readonly<Record<Role, Grant>> =>
  Object.freeze({ owner, manager, staff });

/** PER-10. */
export const PERMISSION_MATRIX: Readonly<Record<Action, Readonly<Record<Role, Grant>>>> = {
  'appointment.view': row(Y, Y, OWN),
  'appointment.create': row(Y, Y, OWN),
  'appointment.edit': row(Y, Y, OWN),
  'appointment.cancel': row(Y, Y, OWN),
  'appointment.noShow': row(Y, Y, OWN),
  'appointment.reopen': row(Y, Y, OWN),
  'appointment.complete': row(Y, Y, OWN),
  'appointment.uncomplete': row(Y, Y, N),
  'appointment.overbook': row(Y, Y, { if: 'team.staffCanOverbook', then: OWN }),
  'appointment.anyStaff': row(Y, Y, N),
  'appointment.markSeen': row(Y, Y, OWN),
  'reminder.send': row(Y, Y, N),
  'schedule.hours': row(Y, N, N),
  'schedule.exceptions': row(Y, Y, N),
  'booking.link': row(Y, Y, Y),
  'client.view': row(Y, Y, REL),
  // Profesional: solo el celular de fichas relacionadas, dentro de la ficha (el email nunca).
  'client.viewContact': row(Y, Y, { if: 'team.staffSeesFullPhone', then: REL }),
  // Profesional: sí al dar un turno (CLI-10), dentro de `appointment.create`.
  'client.create': row(Y, Y, N),
  // Profesional: solo notas (CLI-60).
  'client.edit': row(Y, Y, REL),
  'client.editContact': row(Y, Y, N),
  'client.message': row(Y, Y, N),
  'client.merge': row(Y, Y, N),
  // Encargado: solo fichas sin actividad (CLI-62).
  'client.delete': row(Y, LIM, N),
  'client.anonymize': row(Y, N, N),
  'client.export': row(Y, N, N),
  'cash.view': row(Y, Y, N),
  'sale.create': row(Y, Y, { if: 'team.staffCanCharge', then: OWN }),
  'sale.walkin': row(Y, Y, { if: 'team.staffCanCharge', then: OWN }),
  'sale.editPrice': row(Y, { if: 'sales.priceOverrideMinRole=manager', then: Y }, N),
  // Tope por rol: dueño sin tope; encargado y profesional hasta su máximo (CAJ-13).
  'sale.discount': row(Y, LIM, LIM),
  'sale.backdate': row(Y, Y, N),
  'sale.void': row(Y, { if: 'cash.managerCanVoid', then: Y }, N),
  'sale.voidSettled': row(Y, { if: 'cash.managerCanVoid&!cash.voidSettledRequiresOwner', then: Y }, N),
  'sale.viewCommission': row(Y, N, OWN),
  // Encargado: tope `cash.managerExpenseLimit` (CAJ-72).
  'cash.expense': row(Y, LIM, N),
  'cash.income': row(Y, Y, N),
  // Encargado: gastos e ingresos que cargó él (CAJ-74).
  'cash.voidMovement': row(Y, LIM, N),
  'cashSession.close': row(Y, Y, N),
  'cashSession.reopen': row(Y, N, N),
  'paymentMethod.manage': row(Y, N, N),
  'expenseCategory.manage': row(Y, N, N),
  'giftcard.view': row(Y, Y, N),
  'giftcard.sell': row(Y, Y, N),
  'giftcard.redeem': row(Y, Y, N),
  'giftcard.acceptExpired': row(
    { if: 'giftCards.acceptExpiredRole!=nobody', then: Y },
    { if: 'giftCards.acceptExpiredRole=manager', then: Y },
    N,
  ),
  'giftcard.void': row(Y, N, N),
  'stock.view': row(Y, Y, N),
  'stock.move': row(Y, Y, N),
  'stock.reverse': row(Y, Y, N),
  'product.manage': row(Y, N, N),
  'recipe.manage': row(Y, N, N),
  // Dueño: "—" en la matriz; lo cubre `commission.viewAll` (PER-43).
  'commission.viewOwn': row(Y, N, OWN),
  'commission.viewAll': row(Y, N, N),
  'settlement.create': row(Y, N, N),
  'settlement.void': row(Y, N, N),
  'advance.create': row(Y, { if: 'commissions.managerCanAdvance', then: Y }, N),
  'commissionRule.manage': row(Y, N, N),
  'finance.view': row(Y, N, N),
  'fixedCost.manage': row(Y, N, N),
  'history.manage': row(Y, N, N),
  'ipc.manage': row(Y, N, N),
  'dashboard.view': row(Y, N, N),
  'settings.business': row(Y, N, N),
  'settings.location': row(Y, N, N),
  'location.create': row(Y, N, N),
  'location.archive': row(Y, N, N),
  'team.manage': row(Y, N, N),
  // Sobre otro dueño: solo el titular (PER-04).
  'user.manage': row(LIM, N, N),
  'audit.view': row(Y, N, N),
  'data.exportAll': row(Y, N, N),
  'profile.edit': row(Y, Y, Y),
};

/** Mensaje propio cuando la celda es ✘ (o su parámetro no se cumple); si no figura, el genérico. */
const DENIAL_REASON: Readonly<Partial<Record<Action, Partial<Record<Role, string>>>>> = {
  'sale.create': { staff: 'staff_cannot_charge' },
  'sale.walkin': { staff: 'staff_cannot_charge' },
  'sale.void': { staff: 'void_staff' },
  'sale.voidSettled': { staff: 'void_staff', manager: 'void_settled' },
  'giftcard.redeem': { staff: 'gift_staff' },
  'giftcard.acceptExpired': {
    owner: 'gift_expired_forbidden',
    manager: 'gift_expired_forbidden',
    staff: 'gift_staff',
  },
  'client.edit': { staff: 'client.edit_field' },
  'client.editContact': { staff: 'client.edit_field' },
  'client.anonymize': { manager: 'client.anonymize' },
  'appointment.anyStaff': { staff: 'own_appointments' },
};

/** Mensaje cuando lo "propio" no se cumple (PER-03). */
const OWN_DENIAL_REASON: Readonly<Partial<Record<Action, string>>> = {
  'appointment.view': 'own_appointments',
  'appointment.create': 'own_appointments',
  'appointment.edit': 'own_appointments',
  'appointment.cancel': 'own_appointments',
  'appointment.noShow': 'own_appointments',
  'appointment.reopen': 'own_appointments',
  'appointment.complete': 'own_appointments',
  'appointment.overbook': 'own_appointments',
  'appointment.markSeen': 'own_appointments',
  'sale.create': 'staff_charge_own',
  'sale.walkin': 'staff_charge_own',
};

// Textos que todavía no están en messages.ts.
export const PERMISSION_MESSAGES = {
  close_with_charge: 'Con Caja y cobros activo, el turno se cierra al cobrarlo.',
  staff_payments_label: 'Pagos al personal',
  staff_payments_note: 'El detalle lo ve el dueño',
} as const;

// ───────────────────────── usuario y contexto ─────────────────────────

/** Usuario autenticado (API §1.5). */
export interface UserCtx {
  id: Id;
  role: Role;
  active: boolean;
  /** Dueño: `'all'`. Encargado: `user_locations`. Profesional: `user_locations` (el alcance exige además el vínculo activo). */
  locationIds: readonly Id[] | 'all';
  /** Profesional vinculado en cada local (D-25). */
  staffByLocation: Readonly<Record<Id, { staffId: Id; active: boolean }>>;
}

/** Contexto de la acción. Los campos opcionales que no vienen no restringen (sirven a la web para mostrar u ocultar). */
export interface ActionCtx {
  /** Local de la acción; `null` = acción del negocio (no se mira el alcance del local). */
  locationId: Id | null;
  locationSettings: LocationSettings;
  businessSettings: BusinessSettings;
  modules: Readonly<ModuleState>;
  /** Profesional del recurso existente (turno, línea, comisión). */
  resourceStaffId?: Id | null;
  /** Quién cargó el recurso (movimiento de caja, CAJ-74). */
  resourceCreatedBy?: Id;
  /** Profesional al que se asigna (alta o reasignación); `null` = "Sin preferencia". */
  targetStaffId?: Id | null;
  /** Importe (gasto del encargado, CAJ-72). */
  amount?: Pesos;
  /**
   * Descuento pedido, en % (CAJ-13): `discountLimitPct(discount, discountAmount, discountBase)`, el %
   * tipeado si es `percent` o el equivalente si es `amount`; misma regla que `buildSale`.
   */
  discountPct?: Pct;
  /** La venta tiene alguna línea o propina liquidada (CAJ-60). */
  saleSettled?: boolean;
  /** Nombre del profesional liquidado, para el mensaje de CAJ-60. */
  settledStaffName?: string;
  movementType?: CashMovementType;
  clientHasActivity?: boolean;
  /** La ficha está relacionada con el profesional (CLI-70). */
  clientRelated?: boolean;
  /** Campos que se quieren cambiar de la ficha (CLI-60). */
  clientFields?: readonly string[];
  /** Líneas del cobro (CAJ-04). */
  saleLines?: readonly { kind: LineKind; staffId: Id | null }[];
  /** Destinatario de la propina (`null`/ausente = sin destinatario; CAJ-04). */
  tipStaffId?: Id | null;
  /** Usuario sobre el que se actúa (PER-04). */
  targetUserId?: Id;
  targetUserIsOwner?: boolean;
  actorIsAccountHolder?: boolean;
  /** Vocabulario del rubro para los mensajes (`vocabParams`). */
  vocab?: Record<string, string>;
}

/** PER-02/03: profesional activo vinculado al usuario en el local, o `null`. */
export function staffIdFor(user: UserCtx, locationId: Id | null): Id | null {
  if (locationId === null) return null;
  const link = user.staffByLocation[locationId];
  return link && link.active ? link.staffId : null;
}

function inAssigned(user: UserCtx, locationId: Id): boolean {
  return user.locationIds === 'all' || user.locationIds.includes(locationId);
}

/** PER-02: motivo por el que el local está fuera del alcance, o `null`. */
export function scopeIssue(user: UserCtx, locationId: Id, vocab?: Record<string, string>): Issue | null {
  if (user.role === 'owner') return null;
  if (!inAssigned(user, locationId)) return error('location');
  if (user.role === 'staff' && staffIdFor(user, locationId) === null) return error('staff_unlinked', vocab);
  return null;
}

/** PER-02: el local está en el alcance del usuario (el profesional, además, con vínculo activo). */
export function hasLocation(user: UserCtx, locationId: Id): boolean {
  return user.active && scopeIssue(user, locationId) === null;
}

/** Issue con un texto de `PERMISSION_MESSAGES` (todavía no está en messages.ts). */
function localIssue(code: 'invalid_state' | 'forbidden', reason: keyof typeof PERMISSION_MESSAGES): Issue {
  return issue(code, reason, 'error', undefined, undefined, PERMISSION_MESSAGES[reason]);
}

function forbidden(action: Action): Issue {
  const description = ACTION_DESCRIPTIONS[action];
  return issue('forbidden', action, 'error', { description }, undefined, msg('forbidden', { description }));
}

function deny(action: Action, role: Role, params?: Record<string, unknown>): Issue {
  const reason = DENIAL_REASON[action]?.[role];
  return reason ? error(reason, params) : forbidden(action);
}

function pctText(p: Pct): string {
  return fmtRate(p).replace(/\s*%$/, '');
}

/** CAJ-04: las líneas y la propina que cobra un profesional son suyas. */
function staffChargeOwnOk(me: Id, ctx: ActionCtx): boolean {
  for (const l of ctx.saleLines ?? []) {
    if (l.kind === 'service' && l.staffId !== me) return false;
    if (l.kind === 'product' && l.staffId !== null && l.staffId !== me) return false;
  }
  if (ctx.tipStaffId !== undefined && ctx.tipStaffId !== null && ctx.tipStaffId !== me) return false;
  return true;
}

function ownOk(action: Action, me: Id, ctx: ActionCtx): boolean {
  if (ctx.resourceStaffId !== undefined && ctx.resourceStaffId !== me) return false;
  if (ctx.targetStaffId !== undefined && ctx.targetStaffId !== me) return false;
  if ((action === 'sale.create' || action === 'sale.walkin') && !staffChargeOwnOk(me, ctx)) return false;
  return true;
}

/** Celda efectiva del rol (resuelve los parámetros: si no se cumple, `'no'`). */
export function grantFor(
  role: Role,
  action: Action,
  loc: LocationSettings,
  biz: BusinessSettings,
): Exclude<Grant, object> {
  const g = PERMISSION_MATRIX[action][role];
  if (typeof g === 'string') return g;
  return POLICY_CONDITIONS[g.if](loc, biz) ? g.then : 'no';
}

/**
 * Devuelve el motivo de rechazo (`module_disabled`, `forbidden` con su razón, `staff_unlinked`…) o
 * `null` si se permite. Orden: usuario activo → módulo (PER-05) → alcance (PER-02) → matriz (PER-10)
 * → topes y reglas propias.
 */
export function check(user: UserCtx, action: Action, ctx: ActionCtx): Issue | null {
  if (!user.active) return error('forbidden_generic');

  // PER-05 / MOD-04: módulo apagado, antes de mirar el rol.
  const mod = ACTION_MODULE[action];
  if (mod !== undefined) {
    const list: readonly ModuleKey[] = typeof mod === 'string' ? [mod] : mod;
    if (!list.some((k) => isModuleActive(ctx.modules, k))) return moduleDisabledIssue(list[0] ?? 'sales');
  }
  const off = ACTION_REQUIRES_MODULE_OFF[action];
  if (off !== undefined && isModuleActive(ctx.modules, off)) {
    return localIssue('invalid_state', 'close_with_charge');
  }

  // PER-02: alcance del local.
  if (ctx.locationId !== null && !BUSINESS_ACTIONS.has(action)) {
    const s = scopeIssue(user, ctx.locationId, ctx.vocab);
    if (s) return s;
  }

  const role = user.role;
  const loc = ctx.locationSettings;
  const biz = ctx.businessSettings;
  const grant = grantFor(role, action, loc, biz);

  if (grant === 'no') {
    if (action === 'sale.voidSettled' && role === 'manager')
      return error('void_settled', { name: ctx.settledStaffName ?? '' });
    return deny(action, role, ctx.vocab);
  }

  if (grant === 'own') {
    const me = staffIdFor(user, ctx.locationId);
    if (me === null) return error('staff_unlinked', ctx.vocab);
    if (!ownOk(action, me, ctx)) {
      const reason = OWN_DENIAL_REASON[action];
      return reason ? error(reason) : forbidden(action);
    }
  }

  if (grant === 'related' && ctx.clientRelated === false) return forbidden(action);

  // Topes y reglas propias de algunas acciones.
  switch (action) {
    case 'client.edit':
      // CLI-60: el profesional solo cambia las notas.
      if (role === 'staff' && ctx.clientFields?.some((f) => f !== 'notes')) return error('client.edit_field');
      break;
    case 'client.delete':
      // CLI-62: con actividad, la anonimiza el dueño.
      if (role === 'manager' && ctx.clientHasActivity === true) return error('client.anonymize');
      break;
    case 'sale.discount': {
      // CAJ-13: dueño sin tope; los demás hasta su máximo.
      const max = maxDiscountPctFor(role, biz);
      if (max !== null) {
        const ok = ctx.discountPct === undefined ? max > 0 : withinDiscountCap(ctx.discountPct, max);
        if (!ok) return error('discount_limit', { max: pctText(max) });
      }
      break;
    }
    case 'sale.void':
      // CAJ-60: una venta liquidada la anula quien tenga `sale.voidSettled`.
      if (ctx.saleSettled === true) {
        const settled = check(user, 'sale.voidSettled', ctx);
        if (settled) return settled;
      }
      break;
    case 'cash.expense': {
      // CAJ-72: tope del encargado.
      const limit = loc.cash.managerExpenseLimit;
      if (role === 'manager' && limit !== null && ctx.amount !== undefined && ctx.amount > limit) {
        return error('manager_expense_limit', { amount: fmtARS(limit) });
      }
      break;
    }
    case 'cash.voidMovement':
      // CAJ-74: el encargado anula solo gastos e ingresos que cargó él.
      if (role === 'manager') {
        if (
          ctx.movementType !== undefined &&
          ctx.movementType !== 'expense' &&
          ctx.movementType !== 'income'
        ) {
          return forbidden(action);
        }
        if (ctx.resourceCreatedBy !== undefined && ctx.resourceCreatedBy !== user.id)
          return forbidden(action);
      }
      break;
    case 'user.manage':
      // PER-04: el acceso de otro dueño lo cambia solo el titular.
      if (
        ctx.targetUserIsOwner === true &&
        ctx.targetUserId !== user.id &&
        ctx.actorIsAccountHolder !== true
      ) {
        return error('account_holder_only');
      }
      break;
    default:
      break;
  }
  return null;
}

/** `check(...) === null`. */
export function can(user: UserCtx, action: Action, ctx: ActionCtx): boolean {
  return check(user, action, ctx) === null;
}

// ───────────────────────── PER-04: usuarios ─────────────────────────

export interface UserAccessChange {
  actor: { id: Id; isAccountHolder: boolean };
  target: { id: Id; role: Role; active: boolean };
  /** Lo que cambia: rol nuevo y/o activo. */
  change: { role?: Role; active?: boolean };
  /** Dueños activos hoy (incluido el destino si lo es). */
  activeOwnerCount: number;
}

/**
 * PER-04: nadie se quita a sí mismo el rol de dueño ni se desactiva; el acceso de otro dueño lo
 * cambia solo el titular; tiene que quedar al menos un dueño activo.
 */
export function userAccessChangeIssues(input: UserAccessChange): Issue[] {
  const { actor, target, change } = input;
  const out: Issue[] = [];
  const losesOwner =
    target.role === 'owner' &&
    target.active &&
    ((change.role !== undefined && change.role !== 'owner') || change.active === false);
  if (actor.id === target.id && losesOwner) out.push(error('self_permissions'));
  if (actor.id !== target.id && target.role === 'owner' && !actor.isAccountHolder)
    out.push(error('account_holder_only'));
  if (losesOwner && input.activeOwnerCount <= 1) out.push(error('last_owner'));
  return out;
}

// ───────────────────────── PER-06: navegación ─────────────────────────

export type Section =
  | 'agenda'
  | 'cash'
  | 'clients'
  | 'giftcards'
  | 'stock'
  | 'commissions'
  | 'finance'
  | 'dashboard'
  | 'settings'
  | 'help';

export interface SectionInfo {
  /** Segmento de ruta (UX §2.2). */
  route: string;
  /** Módulo del que depende (`null` = núcleo). */
  module: ModuleKey | null;
  /** Nombre por defecto; "Clientes" usa el vocabulario del rubro ("Pacientes" en salud). */
  label: string;
}

export const SECTIONS: Readonly<Record<Section, Readonly<SectionInfo>>> = {
  agenda: { route: 'agenda', module: null, label: 'Agenda' },
  cash: { route: 'caja', module: 'sales', label: 'Caja' },
  clients: { route: 'clientes', module: null, label: 'Clientes' },
  giftcards: { route: 'gift-cards', module: 'giftcards', label: 'Gift cards' },
  stock: { route: 'stock', module: 'stock', label: 'Stock' },
  commissions: { route: 'comisiones', module: 'commissions', label: 'Comisiones' },
  finance: { route: 'finanzas', module: 'finance', label: 'Finanzas' },
  dashboard: { route: 'tablero', module: 'dashboard', label: 'Tablero' },
  settings: { route: 'ajustes', module: null, label: 'Ajustes' },
  help: { route: 'ayuda', module: null, label: 'Ayuda' },
};

/** Destinos de cada rol en el riel (orden de PER-06). */
export const NAVIGATION: Readonly<Record<Role, readonly Section[]>> = {
  owner: [
    'agenda',
    'cash',
    'clients',
    'giftcards',
    'stock',
    'commissions',
    'finance',
    'dashboard',
    'settings',
    'help',
  ],
  manager: ['agenda', 'cash', 'clients', 'giftcards', 'stock', 'help'],
  staff: ['agenda', 'clients', 'commissions', 'help'],
};

/** Orden por uso de la barra inferior del teléfono (UX §2.2, `ux·P49`). */
export const PHONE_ORDER: Readonly<Record<Role, readonly Section[]>> = {
  owner: [
    'agenda',
    'cash',
    'clients',
    'dashboard',
    'giftcards',
    'stock',
    'commissions',
    'finance',
    'settings',
    'help',
  ],
  manager: ['agenda', 'cash', 'clients', 'stock', 'giftcards', 'help'],
  staff: ['agenda', 'clients', 'commissions', 'help'],
};

export function sectionActive(section: Section, modules: Readonly<ModuleState>): boolean {
  const m = SECTIONS[section].module;
  return m === null || isModuleActive(modules, m);
}

/** PER-06: secciones del riel para el rol, solo las de módulos activos. */
export function navigationFor(role: Role, modules: Readonly<ModuleState>): Section[] {
  return NAVIGATION[role].filter((s) => sectionActive(s, modules));
}

/** Máximo de lugares de la barra inferior. */
export const PHONE_BAR_SLOTS = 5;

/**
 * PER-06: barra inferior del teléfono. Con ≤ 5 destinos van todos y `more` queda vacío; con más,
 * `bar` lleva los 4 primeros y la interfaz agrega "Más" como quinto lugar con `more`.
 */
export function phoneBarFor(role: Role, modules: Readonly<ModuleState>): { bar: Section[]; more: Section[] } {
  const all = PHONE_ORDER[role].filter((s) => sectionActive(s, modules));
  if (all.length <= PHONE_BAR_SLOTS) return { bar: all, more: [] };
  return { bar: all.slice(0, PHONE_BAR_SLOTS - 1), more: all.slice(PHONE_BAR_SLOTS - 1) };
}

// ───────────────────────── visibilidad de datos (PER-40…44) ─────────────────────────

/** CAJ-13: descuento máximo del rol (`null` = sin tope, el dueño). */
export function maxDiscountPctFor(role: Role, businessSettings: BusinessSettings): Pct | null {
  if (role === 'owner') return null;
  return role === 'manager'
    ? businessSettings.sales.discountMaxPctManager
    : businessSettings.sales.discountMaxPctStaff;
}

/** `sale.editPrice` sin contexto de local (CAJ-10): rol ≥ `sales.priceOverrideMinRole`; el profesional nunca. */
export function canEditPriceFor(role: Role, businessSettings: BusinessSettings): boolean {
  return role !== 'staff' && roleAtLeast(role, businessSettings.sales.priceOverrideMinRole);
}

export interface ClientVisibility {
  phone: 'full' | 'masked';
  phoneSearch: 'any' | 'last4';
  email: boolean;
  /** Contactos de reactivación y consentimientos. */
  contacts: boolean;
  scope: 'all' | 'related';
}

/** CLI-70, PER-40/41/44. */
export function clientVisibility(role: Role, locationSettings: LocationSettings): ClientVisibility {
  if (role !== 'staff')
    return { phone: 'full', phoneSearch: 'any', email: true, contacts: true, scope: 'all' };
  return {
    phone: locationSettings.team.staffSeesFullPhone ? 'full' : 'masked',
    phoneSearch: 'last4',
    email: false,
    contacts: false,
    scope: 'related',
  };
}

/** PER-40/41/43 como datos: qué ve cada rol. */
export const ROLE_VISIBILITY = {
  owner: {
    appointments: 'all',
    clients: 'all',
    sales: 'full',
    cash: 'full',
    stock: true,
    commissionRules: 'all',
    staffPayments: 'detail',
    finance: true,
    dashboard: true,
  },
  manager: {
    appointments: 'all',
    clients: 'all',
    sales: 'no_commission',
    cash: 'full',
    stock: true,
    commissionRules: 'none',
    staffPayments: 'aggregated',
    finance: false,
    dashboard: false,
  },
  staff: {
    appointments: 'own',
    clients: 'related',
    sales: 'own',
    cash: 'none',
    stock: false,
    commissionRules: 'own',
    staffPayments: 'own',
    finance: false,
    dashboard: false,
  },
} as const satisfies Record<
  Role,
  {
    appointments: 'all' | 'own';
    clients: 'all' | 'related';
    sales: 'full' | 'no_commission' | 'own';
    cash: 'full' | 'none';
    stock: boolean;
    commissionRules: 'all' | 'none' | 'own';
    staffPayments: 'detail' | 'aggregated' | 'own';
    finance: boolean;
    dashboard: boolean;
  }
>;

/** PER-40/41: regla de comisión de un profesional. */
export function canSeeCommissionRule(user: UserCtx, staffId: Id, locationId: Id): boolean {
  if (!user.active) return false;
  if (user.role === 'owner') return true;
  if (user.role === 'manager') return false;
  return staffIdFor(user, locationId) === staffId;
}

/** Línea de una venta, con los campos que necesita la proyección (el resto pasa tal cual). */
export interface SaleViewLineInput {
  staffId: Id | null;
  net: Pesos;
  unitCost?: Pesos | null;
  cost?: Pesos | null;
}

/** Venta para proyectar por rol (forma del dominio; la API mapea a `SaleDetail`). */
export interface SaleViewInput<L extends SaleViewLineInput = SaleViewLineInput> {
  id: Id;
  businessDate: ISODate;
  paidAt: Instant;
  appointmentId: Id | null;
  clientId: Id | null;
  clientName: string | null;
  status: SaleStatus;
  voidedAt: Instant | null;
  lines: readonly L[];
  tip: { amount: Pesos; staffId: Id | null } | null;
  total: Pesos;
}

/** PER-40: lo que ve el profesional de una venta. */
export interface StaffSaleView<L extends SaleViewLineInput = SaleViewLineInput> {
  id: Id;
  businessDate: ISODate;
  paidAt: Instant;
  appointmentId: Id | null;
  clientId: Id | null;
  clientName: string | null;
  status: SaleStatus;
  voidedAt: Instant | null;
  /** Solo sus líneas, con su comisión, sin costos. */
  lines: L[];
  /** Su propina o 0. */
  tip: Pesos;
  /** Σ net de sus líneas. */
  total: Pesos;
}

/** Proyección por rol: dueño → todo; encargado → sin `commission*` en las líneas; profesional → `StaffSaleView`. */
export type SaleView<S extends SaleViewInput = SaleViewInput> = S | StaffSaleView<S['lines'][number]>;

/** La venta tiene alguna línea o propina del profesional (PER-40: si no, no la ve). */
export function saleIsOwn(staffId: Id | null, sale: Pick<SaleViewInput, 'lines' | 'tip'>): boolean {
  if (staffId === null) return false;
  return (
    sale.lines.some((l) => l.staffId === staffId) ||
    (sale.tip !== null && sale.tip.staffId === staffId && sale.tip.amount > 0)
  );
}

function withoutCommission<L extends object>(line: L): L {
  const out = { ...line } as unknown as Record<string, unknown>;
  for (const k of Object.keys(out)) if (k.startsWith('commission')) out[k] = null;
  return out as unknown as L;
}

export function saleViewFor<S extends SaleViewInput>(
  role: 'owner' | 'manager',
  staffId: Id | null,
  sale: S,
): S;
export function saleViewFor<S extends SaleViewInput>(
  role: 'staff',
  staffId: Id | null,
  sale: S,
): StaffSaleView<S['lines'][number]>;
export function saleViewFor<S extends SaleViewInput>(role: Role, staffId: Id | null, sale: S): SaleView<S>;
/**
 * Reducción de una venta por rol (PER-40/41). Encargado: todo campo de línea que empieza con
 * `commission` (`commission`, `commissionRate`, `commissionBase`, `commissionAmount`…) queda en `null`.
 * Profesional: `{ id, businessDate, paidAt, appointmentId, clientId, clientName, status, voidedAt,
 * lines: solo las suyas (con su comisión, sin `unitCost`/`cost`), tip: la suya o 0, total: Σ net }`.
 */
export function saleViewFor<S extends SaleViewInput>(role: Role, staffId: Id | null, sale: S): SaleView<S> {
  if (role === 'owner') return sale;
  if (role === 'manager') return { ...sale, lines: sale.lines.map((l) => withoutCommission(l)) };
  type L = S['lines'][number];
  const lines: L[] = sale.lines
    .filter((l) => staffId !== null && l.staffId === staffId)
    .map((l) => {
      const out = { ...l } as unknown as Record<string, unknown>;
      if ('unitCost' in out) out.unitCost = null;
      if ('cost' in out) out.cost = null;
      return out as unknown as L;
    });
  const tip = staffId !== null && sale.tip !== null && sale.tip.staffId === staffId ? sale.tip.amount : 0;
  return {
    id: sale.id,
    businessDate: sale.businessDate,
    paidAt: sale.paidAt,
    appointmentId: sale.appointmentId,
    clientId: sale.clientId,
    clientName: sale.clientName,
    status: sale.status,
    voidedAt: sale.voidedAt,
    lines,
    tip,
    total: lines.reduce((acc, l) => acc + l.net, 0),
  };
}

/** Movimiento de caja, con los campos que necesita la proyección. */
export interface CashEntryViewInput {
  type: CashMovementType;
  amount: Pesos;
  methodId: Id;
  /** Profesional del adelanto o del pago de liquidación. */
  staffId?: Id | null;
}

/** PER-42: fila agregada "Pagos al personal" que ve el encargado, una por medio. */
export interface StaffPaymentsRow {
  type: 'staff_payments';
  methodId: Id;
  amount: Pesos;
  count: number;
  label: string;
  note: string;
}

export const STAFF_PAYMENT_TYPES: readonly CashMovementType[] = Object.freeze([
  'staff_advance',
  'commission_payout',
] as const);

/**
 * PER-40/41/42: movimientos de caja que ve cada rol. Dueño: todos. Encargado: los demás tal cual y
 * `staff_advance` + `commission_payout` agregados en una fila por medio (en el orden en que aparece
 * cada medio). Profesional: solo sus adelantos y pagos de liquidación.
 */
export function cashEntriesFor<E extends CashEntryViewInput>(
  role: Role,
  staffId: Id | null,
  entries: readonly E[],
): (E | StaffPaymentsRow)[] {
  const isStaffPayment = (e: E): boolean => STAFF_PAYMENT_TYPES.includes(e.type);
  if (role === 'owner') return [...entries];
  if (role === 'staff')
    return entries.filter((e) => isStaffPayment(e) && staffId !== null && e.staffId === staffId);
  const out: (E | StaffPaymentsRow)[] = [];
  const rows = new Map<Id, StaffPaymentsRow>();
  for (const e of entries) {
    if (!isStaffPayment(e)) {
      out.push(e);
      continue;
    }
    let r = rows.get(e.methodId);
    if (!r) {
      r = {
        type: 'staff_payments',
        methodId: e.methodId,
        amount: 0,
        count: 0,
        label: PERMISSION_MESSAGES.staff_payments_label,
        note: PERMISSION_MESSAGES.staff_payments_note,
      };
      rows.set(e.methodId, r);
    }
    r.amount += e.amount;
    r.count += 1;
  }
  return [...out, ...rows.values()];
}

/** Etiqueta del módulo de una acción (para textos de la interfaz). */
export function actionModuleLabel(action: Action): string | null {
  const m = ACTION_MODULE[action];
  if (m === undefined) return null;
  const k = typeof m === 'string' ? m : m[0];
  return k ? MODULES[k].shortLabel : null;
}
